<script context="module">
    import { currentUser, selectedItem, ratingLoading } from '../../store/store.js';
    import Logotype from '../Logotype.svelte';
    import { slugify } from '../../utils/utils.js';

    const ratingKeyTranslations = {
        overall_rating: 'Helhetsbetyg',
        loans_per_sqm_rating: 'Lån',
        saving_per_sqm_rating: 'Sparande',
        solidity_rating: 'Soliditet',
        rental_income_from_commercial_premises_per_sqm_rating: 'Affärsintäkter',
        living_fee_per_sqm_rating: 'Avgifter',
        living_area_sqm_rating: 'Boarea',
        number_of_apartments_rating: 'Antal lägenheter',
        commercial_area_sqm_rating: 'Affärsarea',
        liquid_assets_per_sqm_rating: 'Likvida tillgångar',
        energy_cost_per_sqm_rating: 'Energikostnader',
        operating_expenses_per_sqm_rating: 'Driftskostnader',
        profit_after_financial_items_rating: 'Resultat',
        owns_the_land_rating: 'Markägande',
        genuine_house_association_rating: 'Äkta förening',
        age_of_association_rating: 'Föreningens ålder',
    };

    const ratingExplanations = {
        overall_rating: 'Helhetsbetyget är en sammanvägning av alla delbetyg med tyngdpunkt på skuldsättning. Det ger en snabb överblick över föreningens ekonomiska hälsa och sätts i relation till övriga föreningar på marknaden.',
        solidity_rating: 'Soliditet = eget kapital / totala tillgångar. Hög soliditet innebär att föreningen har en stark ekonomisk grund. Negativ soliditet – där skulderna överstiger tillgångarna – är en varningssignal.',
        loans_per_sqm_rating: 'Skulder per kvm är det viktigaste nyckeltalet och väger tyngst i helhetsbetyget. Lägre skuld innebär lägre räntekostnader och tryggare ekonomi. Betyget sätts i relation till marknaden – vid nyproduktion är höga lån vanligt och tas i beaktning.',
        saving_per_sqm_rating: 'Sparande per kvm visar kassaflödet – hur mycket föreningen har kvar efter löpande kostnader. Högt sparande bygger buffert för underhåll och amortering. Betyget speglar föreningens sparförmåga relativt jämförbara föreningar.',
        liquid_assets_per_sqm_rating: 'Likvida medel per kvm är kassa och bank i förhållande till boarean. God likviditet ger trygghet vid akuta kostnader utan att behöva ta dyra lån.',
        living_fee_per_sqm_rating: 'Månadsavgiften per kvm jämförs i relation till marknaden. En låg avgift är inte alltid bra – den kan dölja eftersatt underhåll eller otillräckligt sparande.',
        living_area_sqm_rating: 'Större boarea sprider fasta kostnader på fler kvm. Stordriftsfördelar gör att kostnader för t.ex. förvaltning och renoveringar blir proportionellt lägre.',
        number_of_apartments_rating: 'Fler lägenheter ger ekonomisk stabilitet – kostnader för oförutsedda händelser delas på fler. Mycket små föreningar är mer sårbara vid t.ex. utebliven avgiftsbetalning.',
        commercial_area_sqm_rating: 'Lokaler genererar hyresintäkter som sänker bostadsavgifterna. Andelen lokal/boarea avgör även skatteklassificeringen (äkta/oäkta förening).',
        rental_income_from_commercial_premises_per_sqm_rating: 'Lokalintäkter per kvm visar hur mycket hyresintäkter som fördelas på boarean. Höga intäkter kompenserar för andra kostnader och håller nere avgifterna.',
        energy_cost_per_sqm_rating: 'Energikostnad per kvm speglar fastighetens energieffektivitet. Låga kostnader tyder på bra isolering och effektiva system. Höga kostnader kan indikera framtida investeringsbehov.',
        operating_expenses_per_sqm_rating: 'Driftskostnader per kvm inkluderar förvaltning, städning, sophämtning m.m. Låga kostnader tyder på effektiv drift och god ekonomisk hushållning.',
        profit_after_financial_items_rating: 'Resultatet efter finansiella poster visar om föreningen går med vinst eller förlust per kvm. Positivt resultat bygger buffert men väger inte tungt i helhetsbetyget.',
        owns_the_land_rating: 'Om föreningen äger marken slipper den betala tomträttsavgäld till kommunen. Tomträtt innebär att avgälden omförhandlas var 10–20:e år, vilket kan ge kraftiga höjningar och i sin tur höjda månadsavgifter.',
        genuine_house_association_rating: 'En äkta bostadsrättsförening kräver att minst 60 % av ytan upplåts till boende medlemmar. Fördelen är lägre kapitalvinstskatt vid försäljning. En oäkta förening beskattas hårdare och kan innebära förmånsbeskattning.',
        age_of_association_rating: 'Föreningens ålder ger en fingervisning om underhållsbehov. Äldre föreningar kan ha kommande stora renoveringar (tak, stammar, hissar). Nyare föreningar har modern standard men ofta hög skuldsättning från byggnationen.',
    };

    function getTranslatedKey(key) {
        return ratingKeyTranslations[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }

    function calculateStars(rating) {
        return Math.round((rating / 100) * 10);
    }

    export async function rateBrf(coreId, livingAreaSqm = null, user = null) {
        ratingLoading.set(true);
        let url = 'https://boanalys-backend-324523837899.europe-north1.run.app/rate_brf';
        const queryParams = new URLSearchParams({ core_id: coreId });
        
        const body = livingAreaSqm !== null ? { living_area_sqm: livingAreaSqm } : {};
        
        const response = await fetch(`${url}?${queryParams.toString()}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${user?.accessToken}`,
        },
        body: JSON.stringify(body),
        });
    
        if (!response.ok) {
        throw new Error('Failed to trigger backend function');
        }
    
        console.log('Rated core_id:', coreId);
        ratingLoading.set(false);
        return coreId;
    }

	function getSortedRatings(ratingObj, overall_rating) {
		if (!Object.entries) {
			Object.entries = function(obj) {
				return Object.keys(obj).map(key => [key, obj[key]]);
			};
		}
		let ratings = Object.entries(ratingObj);
		if (overall_rating === true) {
			ratings = ratings.map(([key, value]) => ({ key, ...value })).filter(item => item.rating !== undefined && item.rating !== null);
		}
		else {
			ratings = ratings.map(([key, value]) => ({ key, ...value })).filter(item => item.rating !== undefined && item.rating !== null && item.key !== "overall_rating");

		}
		ratings = ratings.sort((a, b) => b.rating - a.rating);
        return ratings;
    }
</script>

<script>
    import { Link } from 'svelte-routing';
    import { get } from 'svelte/store';

    export let showDetails = false;
    export let maxExplanationChars = 260;
    
    let flippedCards = new Set();
    
    function toggleFlip(key) {
        flippedCards = new Set(flippedCards);
        if (flippedCards.has(key)) flippedCards.delete(key);
        else flippedCards.add(key);
        flippedCards = flippedCards;
    }
    
    let retryFailed = false;
    let isRetrying = false;
    let inputLivingArea = '';

    async function handleRetryRating() {
        const item = get(selectedItem);
        if (!item) return;

        isRetrying = true;
        retryFailed = false;

        try {
            const livingArea = item.living_area_sqm !== null ? null : parseFloat(inputLivingArea);
            const user = get(currentUser);
            await rateBrf(item.core_id, livingArea, user);
            window.location.reload();
        } catch (error) {
            console.error('Failed to retrigger rating:', error);
            retryFailed = true;
        } finally {
            isRetrying = false;
        }
    }

    function truncate(text, maxChars) {
        if (!text) return '';
        if (!maxChars || text.length <= maxChars) return text;
        return text.slice(0, maxChars).trimEnd() + '…';
    }
</script>

{#if $selectedItem}
    <div class="rating-container">
        <div class="dashboard-container">
            <main class="main-content">
                <div class="content-wrapper">
                    {#if $selectedItem?.rating?.overall_rating}
                        <div class="ai-rating-card key-value-box">
                            <div class="metric-header">
                                <h6>{getTranslatedKey('overall_rating')}</h6>
                            </div>
                            <div class="stars">
                                {#each Array(5) as _, i}
                                    {#if $selectedItem.rating.overall_rating && i < Math.floor(calculateStars($selectedItem.rating.overall_rating.rating || 0) / 2)}
                                    <div class="star filled"></div>
                                    {:else if $selectedItem.rating.overall_rating && i === Math.floor(calculateStars($selectedItem.rating.overall_rating.rating || 0) / 2) && calculateStars($selectedItem.rating.overall_rating.rating || 0) % 2 !== 0}
                                    <div class="star half-filled"></div>
                                    {:else}
                                    <div class="star"></div>
                                    {/if}
                                {/each}
                            </div>
                            <div class="metric-value overall_rating">
                                {$selectedItem.rating.overall_rating ? $selectedItem.rating.overall_rating.rating : "--"}
                                <span class="rating-scale"> / 100</span>
                            </div>
                            <div class="explanation_rating">
                                <p>
                                    {#if $selectedItem.rating.overall_rating}
                                        {truncate(
                                            getSortedRatings($selectedItem.rating, true).find(r => r.key === 'overall_rating')?.reason || "",
                                            maxExplanationChars
                                        )}
                                    {/if}
                                </p>
                                {#if $selectedItem?.risk_for_fee_increase}
                                    <div class="risk-row" aria-label="Risk för avgiftshöjning">
                                        <span class="risk-label">Risk för avgiftshöjning</span>
                                        <span class="risk-pill">
                                            {truncate($selectedItem.risk_for_fee_increase, 40)}
                                        </span>
                                    </div>
                                {/if}
                                {#if !showDetails}
                                    <Link to={`/brf/${$selectedItem.core_id}/${slugify($selectedItem.brf_name)}/report`} class="action-button">Visa rapport</Link>
                                {:else}
                                    <div class="detailed-ratings">
                                        <h3 class="detailed-ratings-title">Detaljerad analys</h3>
                                        <div class="ratings-grid">
                                            {#each getSortedRatings($selectedItem.rating, false) as item}
                                                {@const ratingColor = item.rating >= 80 ? 'excellent' : item.rating >= 60 ? 'good' : item.rating >= 40 ? 'moderate' : item.rating >= 20 ? 'poor' : 'critical'}
                                                {@const isFlipped = flippedCards.has(item.key)}
                                                <div 
                                                    class="rating-card rating-{ratingColor} flip-card" 
                                                    class:flipped={isFlipped}
                                                    role="button"
                                                    tabindex="0"
                                                    on:click={() => toggleFlip(item.key)}
                                                    on:keydown={(e) => e.key === 'Enter' && toggleFlip(item.key)}
                                                >
                                                    <div class="flip-card-inner">
                                                        <div class="flip-card-front">
                                                            <div class="rating-card-header">
                                                                <h4>{getTranslatedKey(item.key)}</h4>
                                                                <div class="rating-badge rating-badge-{ratingColor}">
                                                                    {item.rating}
                                                                </div>
                                                            </div>
                                                            <p class="rating-reason">{item.reason}</p>
                                                            <span class="flip-hint">Klicka för mer info</span>
                                                        </div>
                                                        <div class="flip-card-back">
                                                            <h4>{getTranslatedKey(item.key)}</h4>
                                                            <div class="explanation-scroll">
                                                                <p class="rating-explanation">{ratingExplanations[item.key] || 'Ingen förklaring tillgänglig.'}</p>
                                                            </div>
                                                            <span class="flip-hint">Klicka för att vända tillbaka</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            {/each}
                                        </div>
                                    </div>
                                {/if}
                            </div>
                        </div>
                    {:else if $ratingLoading}
                        <div class="ai-rating-card loading-card">
                            <h6 style="text-align: center; margin-bottom: 15px;">Betygsättning pågår...</h6>
                            <div class="loading-analysis">
                                <Logotype />
                            </div>
                        </div>
                    {:else}
                        <div class="ai-rating-card missing-card">
                            {#if retryFailed}
                                <p style="text-align: center; font-size: 0.9rem; color: #6c757d; margin-bottom: 10px;">
                                    Betygsättningen misslyckades. Vänligen kontakta oss via e-post.
                                </p>
                                <p style="text-align: center; font-size: 2rem; color: #dc3545;"><i class="fa-solid fa-circle-exclamation"></i></p>
                            {:else if $selectedItem.living_area_sqm !== null}
                                <p style="text-align: center; font-size: 0.9rem; color: #6c757d; margin-bottom: 15px;">
                                    Betyg saknas och kan ta ett par dagar att åtgärdas.
                                </p>
                                <p style="text-align: center; font-size: 2rem; color: #6c757d; margin-bottom: 15px;">
                                    <i class="fa-solid fa-hourglass-half"></i>
                                </p>
                                {#if !isRetrying}
                                    <button class="retry-button" on:click={handleRetryRating}>
                                        <i class="fa-solid fa-rotate-right"></i> Försök igen
                                    </button>
                                {:else}
                                    <button class="retry-button" disabled>
                                        <i class="fa-solid fa-spinner fa-spin"></i> Försöker igen...
                                    </button>
                                {/if}
                            {:else}
                                <p style="text-align: center; font-size: 0.9rem; color: #6c757d; margin-bottom: 15px;">
                                    Betyg saknas och kan ta längre tid att åtgärdas då boarea saknas.
                                </p>
                                <div class="living-area-input">
                                    <label for="living-area">Ange boarea (m²):</label>
                                    <input 
                                        id="living-area"
                                        type="number" 
                                        bind:value={inputLivingArea} 
                                        placeholder="t.ex. 75"
                                        min="1"
                                    />
                                </div>
                                {#if !isRetrying}
                                    <button 
                                        class="retry-button" 
                                        on:click={handleRetryRating}
                                        disabled={!inputLivingArea || parseFloat(inputLivingArea) <= 0}
                                    >
                                        <i class="fa-solid fa-rotate-right"></i> Försök igen
                                    </button>
                                {:else}
                                    <button class="retry-button" disabled>
                                        <i class="fa-solid fa-spinner fa-spin"></i> Försöker igen...
                                    </button>
                                {/if}
                            {/if}
                        </div>
                    {/if}
                </div>
            </main>
        </div>
    </div>
{/if}

<style>
    @font-face {
        font-family: "MuseoModerna";
        src: url("/MuseoModerno.ttf") format("truetype");
        font-weight: normal;
        font-style: normal;
    }

    .rating-container {
        font-family: var(--test-font-family);
        width: 100%;
        max-width: 100%;
        margin: 0 auto;
        box-sizing: border-box;
        overflow: visible;
    }

    .rating-container :global(h1) {
        font-family: "MuseoModerna", sans-serif;
        font-size: 2rem;
        font-weight: 600;
        color: #333;
        margin: 0 0 0 0;
        line-height: 1.2;
        letter-spacing: -0.02em;
    }

    .rating-container :global(h5) {
        font-family: var(--test-font-family);
        font-size: 1rem;
        color: #495057;
        margin-bottom: 12px;
        padding-bottom: 6px;
        border-bottom: 1px solid #e9ecef;
        display: flex;
        align-items: center;
        gap: 0.5rem;
    }

    .rating-container :global(p) {
        font-family: var(--test-font-family);
        font-size: 1rem;
        font-weight: 400;
        color: #555;
        line-height: 1.6;
        margin: 0 0 0 0;
    }

    .header-container {
        margin: 1rem auto;
        max-width: 1200px;
        background: rgba(255, 255, 255, 0.4);
        backdrop-filter: blur(40px) saturate(180%);
        -webkit-backdrop-filter: blur(40px) saturate(180%);
        border: 1px solid rgba(255, 255, 255, 0.5);
        border-radius: 24px;
        overflow: hidden;
        position: relative;
        z-index: 1;
        box-shadow:
            0 8px 32px rgba(0, 0, 0, 0.08),
            inset 0 1px 0 rgba(255, 255, 255, 0.6),
            inset 0 -1px 0 rgba(255, 255, 255, 0.2);
    }

    .top-header {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        padding: 1rem 2rem;
        background: transparent;
    }

    .logo-brand {
        display: flex;
        align-items: center;
        gap: 0.75rem;
    }

    .header-divider {
        height: 1px;
        background: linear-gradient(
            to right,
            transparent 0%,
            rgba(255, 255, 255, 0.3) 20%,
            rgba(255, 255, 255, 0.3) 80%,
            transparent 100%
        );
        margin: 0 1rem;
    }

    .header-logo {
        width: 50px;
        height: auto;
    }

    .header-title {
        font-size: 1.25rem;
        font-weight: 500;
        color: #333;
        margin-left: auto;
    }

    .dashboard-container {
        display: flex;
        flex-direction: column;
        font-family: var(--test-font-family);
        width: 100%;
        max-width: 100%;
        overflow: visible;
    }

    .main-content {
        background: transparent;
        overflow: visible;
    }

    .content-wrapper {
        max-width: 1200px;
        margin: 0 auto;
        width: 100%;
        box-sizing: border-box;
        padding: 0;
        overflow: visible;
    }

    .ai-rating-card {
        background-color: var(--box-color);
        border-radius: 12px;
        padding: 2rem;
        border: none;
        box-shadow: 0 2px 4px 0 rgba(4, 4, 52, .06), 0 0 2px 0 rgba(91, 95, 107, .24);
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: center;
        box-sizing: border-box;
        margin-left: auto;
        margin-right: auto;
        max-width: 100%;
        transition: transform 0.3s ease, box-shadow 0.3s ease, background-color 0.3s ease;
    }

    .ai-rating-card:hover {
        box-shadow: 0 8px 12px 0 rgba(4, 4, 52, 0.1), 0 0 4px 0 rgba(91, 95, 107, 0.3);
    }

    .metric-header {
        display: flex;
        justify-content: center;
        align-items: center;
        margin-bottom: 12px;
    }

    .metric-header h6 {
        margin: 0;
        font-size: 0.9rem;
        color: #666;
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: 1px;
    }

    .stars {
        display: flex;
        gap: 5px;
        justify-content: center;
        margin-bottom: 10px;
    }

    .star {
        width: 28px;
        height: 28px;
        background-color: #ddd;
        clip-path: polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%);
    }

    .star.filled {
        background-color: gold;
    }

    .star.half-filled {
        background: linear-gradient(90deg, gold 50%, #ddd 50%);
    }

    .metric-value.overall_rating {
        font-size: 10rem;
        font-weight: 700;
        color: #333;
        line-height: 1;
        margin-bottom: 30px;
        margin-top: 10px;
    }

    .rating-scale {
        font-size: 1.5rem;
        font-weight: 400;
        color: #6c757d;
        margin-left: -40px;
    }

    .explanation_rating {
        font-size: 0.95rem;
        color: #495057;
        line-height: 1.6;
        max-width: 90%;
        text-align: center;
        cursor: pointer;
    }

    .explanation_rating p {
        margin-bottom: 12px;
        max-height: 4.8em;
        overflow: hidden;
    }

    .risk-row {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        font-size: 0.8rem;
        color: #6c757d;
        margin-bottom: 4px;
        flex-wrap: wrap;
        justify-content: center;
    }

    .risk-label {
        text-transform: uppercase;
        letter-spacing: 0.06em;
    }

    .risk-pill {
        padding: 4px 10px;
        border-radius: 999px;
        background: #f1f3f5;
        color: #495057;
        font-weight: 500;
        max-width: 180px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .action-button {
        display: inline-block;
        padding: 10px 20px;
        background: rgba(67, 97, 238, 0.8);
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
        color: white;
        border: 1px solid rgba(255, 255, 255, 0.3);
        border-radius: 12px;
        text-decoration: none;
        font-weight: 600;
        transition: all 0.3s ease;
        font-size: 1rem;
        box-shadow: 0 4px 16px rgba(67, 97, 238, 0.3);
        margin-top: 10px;
    }

    .action-button:hover {
        background: rgba(67, 97, 238, 0.9);
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(67, 97, 238, 0.4);
    }

    .detailed-ratings {
        margin-top: 30px;
        width: 100%;
        border-top: 2px solid #e9ecef;
        padding-top: 25px;
    }

    .detailed-ratings-title {
        font-size: 1.5rem;
        font-weight: 600;
        color: #333;
        margin: 0 0 20px 0;
        text-align: center;
    }

    .ratings-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
        gap: 1.25rem;
        width: 100%;
    }

    .rating-card {
        background: rgba(255, 255, 255, 0.4);
        backdrop-filter: blur(20px) saturate(180%);
        -webkit-backdrop-filter: blur(20px) saturate(180%);
        border-radius: 20px;
        border: 1px solid rgba(255, 255, 255, 0.5);
        transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        box-shadow:
            0 8px 32px rgba(0, 0, 0, 0.08),
            inset 0 1px 0 rgba(255, 255, 255, 0.6),
            inset 0 -1px 0 rgba(255, 255, 255, 0.2);
        position: relative;
    }

    .rating-card:not(.flip-card) {
        overflow: hidden;
    }

    .flip-card {
        cursor: pointer;
        perspective: 1000px;
        min-height: 380px;
        overflow: hidden;
    }

    .flip-card-inner {
        position: relative;
        width: 100%;
        height: 100%;
        min-height: 380px;
        transition: transform 0.6s;
        transform-style: preserve-3d;
    }

    .flip-card.flipped .flip-card-inner {
        transform: rotateY(180deg);
    }

    .flip-card-front,
    .flip-card-back {
        position: absolute;
        width: 100%;
        height: 100%;
        min-height: 380px;
        padding: 1.5rem;
        -webkit-backface-visibility: hidden;
        backface-visibility: hidden;
        display: flex;
        flex-direction: column;
        box-sizing: border-box;
        overflow: hidden;
    }

    .explanation-scroll {
        flex: 1;
        min-height: 0;
        overflow-y: auto;
    }

    .flip-card-back {
        transform: rotateY(180deg);
    }

    .flip-card-back h4 {
        font-size: 1rem;
        font-weight: 600;
        color: #1a1a1a;
        margin: 0 0 0.75rem 0;
        flex-shrink: 0;
    }

    .rating-explanation {
        font-size: 0.9rem;
        color: #2d2d2d;
        line-height: 1.6;
        margin: 0;
    }

    .flip-hint {
        font-size: 0.75rem;
        color: #6c757d;
        margin-top: auto;
        padding-top: 0.5rem;
        flex-shrink: 0;
    }

    .rating-card::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 1px;
        background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.8),
            transparent
        );
    }

    .rating-card:hover {
        transform: translateY(-4px);
        background: rgba(255, 255, 255, 0.5);
        box-shadow:
            0 12px 40px rgba(0, 0, 0, 0.12),
            inset 0 1px 0 rgba(255, 255, 255, 0.7),
            inset 0 -1px 0 rgba(255, 255, 255, 0.3);
        border-color: rgba(255, 255, 255, 0.7);
    }

    .rating-card-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 0.75rem;
    }

    .rating-card-header h4 {
        font-size: 1rem;
        font-weight: 600;
        color: #1a1a1a;
        margin: 0;
        flex: 1;
        text-shadow: 0 1px 2px rgba(255, 255, 255, 0.8);
    }

    .rating-badge {
        font-size: 1.25rem;
        font-weight: 700;
        padding: 0.5rem 1rem;
        border-radius: 14px;
        min-width: 60px;
        text-align: center;
        border: 1px solid rgba(255, 255, 255, 0.3);
        transition: all 0.3s ease;
    }

    .rating-badge:hover {
        transform: scale(1.05);
    }

    .rating-reason {
        font-size: 0.9rem;
        color: #2d2d2d;
        line-height: 1.6;
        margin: 0;
        text-shadow: 0 1px 1px rgba(255, 255, 255, 0.6);
    }

    .rating-excellent {
        background: rgba(40, 167, 69, 0.15);
        border-color: rgba(40, 167, 69, 0.3);
        box-shadow:
            0 8px 32px rgba(40, 167, 69, 0.15),
            inset 0 1px 0 rgba(255, 255, 255, 0.6),
            inset 0 -1px 0 rgba(40, 167, 69, 0.2);
    }

    .rating-excellent:hover {
        background: rgba(40, 167, 69, 0.2);
        border-color: rgba(40, 167, 69, 0.4);
        box-shadow:
            0 12px 40px rgba(40, 167, 69, 0.2),
            inset 0 1px 0 rgba(255, 255, 255, 0.7),
            inset 0 -1px 0 rgba(40, 167, 69, 0.3);
    }

    .rating-excellent .rating-badge-excellent {
        background: linear-gradient(135deg, rgba(40, 167, 69, 0.9), rgba(40, 167, 69, 1));
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
        color: white;
        box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
    }

    .rating-good {
        background: rgba(23, 162, 184, 0.15);
        border-color: rgba(23, 162, 184, 0.3);
        box-shadow:
            0 8px 32px rgba(23, 162, 184, 0.15),
            inset 0 1px 0 rgba(255, 255, 255, 0.6),
            inset 0 -1px 0 rgba(23, 162, 184, 0.2);
    }

    .rating-good:hover {
        background: rgba(23, 162, 184, 0.2);
        border-color: rgba(23, 162, 184, 0.4);
        box-shadow:
            0 12px 40px rgba(23, 162, 184, 0.2),
            inset 0 1px 0 rgba(255, 255, 255, 0.7),
            inset 0 -1px 0 rgba(23, 162, 184, 0.3);
    }

    .rating-good .rating-badge-good {
        background: linear-gradient(135deg, rgba(23, 162, 184, 0.9), rgba(23, 162, 184, 1));
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
        color: white;
        box-shadow: 0 4px 12px rgba(23, 162, 184, 0.3);
    }

    .rating-moderate {
        background: rgba(255, 193, 7, 0.15);
        border-color: rgba(255, 193, 7, 0.3);
        box-shadow:
            0 8px 32px rgba(255, 193, 7, 0.15),
            inset 0 1px 0 rgba(255, 255, 255, 0.6),
            inset 0 -1px 0 rgba(255, 193, 7, 0.2);
    }

    .rating-moderate:hover {
        background: rgba(255, 193, 7, 0.2);
        border-color: rgba(255, 193, 7, 0.4);
        box-shadow:
            0 12px 40px rgba(255, 193, 7, 0.2),
            inset 0 1px 0 rgba(255, 255, 255, 0.7),
            inset 0 -1px 0 rgba(255, 193, 7, 0.3);
    }

    .rating-moderate .rating-badge-moderate {
        background: linear-gradient(135deg, rgba(255, 193, 7, 0.9), rgba(255, 193, 7, 1));
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
        color: #333;
        box-shadow: 0 4px 12px rgba(255, 193, 7, 0.3);
    }

    .rating-poor {
        background: rgba(253, 126, 20, 0.15);
        border-color: rgba(253, 126, 20, 0.3);
        box-shadow:
            0 8px 32px rgba(253, 126, 20, 0.15),
            inset 0 1px 0 rgba(255, 255, 255, 0.6),
            inset 0 -1px 0 rgba(253, 126, 20, 0.2);
    }

    .rating-poor:hover {
        background: rgba(253, 126, 20, 0.2);
        border-color: rgba(253, 126, 20, 0.4);
        box-shadow:
            0 12px 40px rgba(253, 126, 20, 0.2),
            inset 0 1px 0 rgba(255, 255, 255, 0.7),
            inset 0 -1px 0 rgba(253, 126, 20, 0.3);
    }

    .rating-poor .rating-badge-poor {
        background: linear-gradient(135deg, rgba(253, 126, 20, 0.9), rgba(253, 126, 20, 1));
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
        color: white;
        box-shadow: 0 4px 12px rgba(253, 126, 20, 0.3);
    }

    .rating-critical {
        background: rgba(220, 53, 69, 0.15);
        border-color: rgba(220, 53, 69, 0.3);
        box-shadow:
            0 8px 32px rgba(220, 53, 69, 0.15),
            inset 0 1px 0 rgba(255, 255, 255, 0.6),
            inset 0 -1px 0 rgba(220, 53, 69, 0.2);
    }

    .rating-critical:hover {
        background: rgba(220, 53, 69, 0.2);
        border-color: rgba(220, 53, 69, 0.4);
        box-shadow:
            0 12px 40px rgba(220, 53, 69, 0.2),
            inset 0 1px 0 rgba(255, 255, 255, 0.7),
            inset 0 -1px 0 rgba(220, 53, 69, 0.3);
    }

    .rating-critical .rating-badge-critical {
        background: linear-gradient(135deg, rgba(220, 53, 69, 0.9), rgba(220, 53, 69, 1));
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
        color: white;
        box-shadow: 0 4px 12px rgba(220, 53, 69, 0.3);
    }

    .loading-card {
        padding: 30px 16px;
    }

    .loading-analysis {
        display: flex;
        justify-content: center;
        align-items: center;
        opacity: 0.7;
    }

    .loading-analysis :global(svg) {
        width: 60px;
        height: auto;
    }

    .missing-card {
        padding: 30px 16px;
        min-height: 150px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        text-align: center;
    }

    .retry-button {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 10px 20px;
        background: rgba(67, 97, 238, 0.8);
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
        color: white;
        border: 1px solid rgba(255, 255, 255, 0.3);
        border-radius: 12px;
        font-weight: 600;
        transition: all 0.3s ease;
        font-size: 1rem;
        box-shadow: 0 4px 16px rgba(67, 97, 238, 0.3);
        cursor: pointer;
        margin-top: 10px;
    }

    .retry-button:hover:not(:disabled) {
        background: rgba(67, 97, 238, 0.9);
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(67, 97, 238, 0.4);
    }

    .retry-button:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    .living-area-input {
        display: flex;
        flex-direction: column;
        gap: 8px;
        margin-bottom: 10px;
        width: 100%;
        max-width: 300px;
    }

    .living-area-input label {
        font-size: 0.9rem;
        color: #495057;
        font-weight: 500;
    }

    .living-area-input input {
        padding: 10px 15px;
        border: 1px solid #ced4da;
        border-radius: 8px;
        font-size: 1rem;
        transition: border-color 0.3s ease, box-shadow 0.3s ease;
    }

    .living-area-input input:focus {
        outline: none;
        border-color: rgba(67, 97, 238, 0.8);
        box-shadow: 0 0 0 3px rgba(67, 97, 238, 0.1);
    }

    @media (max-width: 992px) {
        .header-title {
            font-size: 1.1rem;
            margin-left: 0;
        }
    }

    @media (max-width: 576px) {
        .header-container {
            margin: 0.5rem auto;
            border-radius: 20px;
        }

        .top-header {
            padding: 1rem;
            flex-direction: column;
            align-items: center;
            gap: 1rem;
        }

        .logo-brand {
            justify-content: center;
        }

        .header-title {
            font-size: 1rem;
            margin-left: 0;
            text-align: center;
        }

        .header-divider {
            margin: 0 1rem;
        }

        .ai-rating-card {
            padding: 1rem;
        }

        .ratings-grid {
            grid-template-columns: 1fr;
            gap: 1rem;
        }

        .detailed-ratings-title {
            font-size: 1.25rem;
        }

        .flip-card.flipped .flip-card-front {
            visibility: hidden;
            pointer-events: none;
        }
    }
</style>
